import React, { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useUiFeedback } from "../../../context/ui-feedback/ui-feedback";
import { createErrorFeedback } from "../../../context/ui-feedback/action";
import { useSearchHistory } from "../../../api/service-history-api";
import { PageLayout } from "../../common/page-layout/page-layout";
import { ControllerRow } from "../../common/controller-row/controller-row";
import { ServicHistorySearch } from "./component/service-history-search";
import { ServiceHistoryTable } from "./component/service-history-table";
import { Pager } from "../../common/pager/pager";

export const ServiceHistory = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const localSearchUUID = useRef(null);
  const [errorInLoading, setErrorInLoading] = useState(false);
  const [listQuery, setListQuery] = useState({
    query: {
      limit: 10,
      offset: 0,
      clientName: null,
      machineNumber: null,
      serviceWorksheetNo: null,
      salesOrderNo: null,
      date: null,
    },
    allCount: null,
    results: null,
  });

  const searchHistory = useSearchHistory();

  useEffect(() => {
    const loadHistory = async () => {
      setErrorInLoading(false);
      setListQuery({
        ...listQuery,
        results: null,
      });
      try {
        const search = {
          offset: listQuery.query.offset,
          limit: listQuery.query.limit,
          clientName: listQuery.query.clientName,
          machineNumber: listQuery.query.machineNumber,
          serviceWorksheetNo: listQuery.query.serviceWorksheetNo,
          salesOrderNo: listQuery.query.salesOrderNo,
          date: listQuery.query.date,
        };
        await searchHistory(search, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            if (data.histories.length === 0 && data.allCount > 0) {
              setListQuery({
                ...listQuery,
                query: {
                  ...listQuery.query,
                  offset:
                    (Math.ceil(data.allCount / listQuery.limit) - 1) *
                    listQuery.limit,
                },
              });
              localSearchUUID.current = null;
            } else {
              setListQuery({
                ...listQuery,
                allCount: data.allCount,
                results: data.histories,
              });
            }
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          setErrorInLoading(true);
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.servicehistory.exception.listhistory.title",
            }),
            intl.formatMessage({
              id: "page.servicehistory.exception.listhistory.description",
            }),
          );
        }
      }
    };

    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      localSearchUUID.current === null &&
      checkNotEmpy([
        intl,
        listQuery,
        navigate,
        searchHistory,
        uiFeedbackDispatch,
      ])
    ) {
      localSearchUUID.current = new Date().getTime();
      loadHistory();
    }
  }, [
    localSearchUUID,
    listQuery.query,
    intl,
    listQuery,
    navigate,
    searchHistory,
    uiFeedbackDispatch,
  ]);

  const changePage = (page) => {
    setListQuery({
      ...listQuery,
      query: {
        ...listQuery.query,
        offset: page * listQuery.query.limit,
      },
    });
    localSearchUUID.current = null;
  };

  const handleSearch = (searchParam) => {
    setListQuery({
      ...listQuery,
      query: {
        ...listQuery.query,
        limit: 10,
        offset: 0,
        clientName: searchParam.clientName,
        machineNumber: searchParam.machineNumber,
        serviceWorksheetNo: searchParam.serviceWorksheetNo,
        salesOrderNo: searchParam.salesOrderNo,
        date: searchParam.date,
      },
    });
    localSearchUUID.current = null;
  };

  const buildPaths = () => {
    return [
      {
        link: "/",
        title: intl.formatMessage({ id: "page.welcome.title" }),
      },
    ];
  };

  return (
    <PageLayout
      paths={buildPaths()}
      title={intl.formatMessage({ id: "page.servicehistory.title" })}
    >
      <>
        <ControllerRow>
          <ServicHistorySearch
            clientName={listQuery.query.clientName}
            machineNumber={listQuery.query.machineNumber}
            serviceWorksheetNo={listQuery.query.serviceWorksheetNo}
            salesOrderNo={listQuery.query.salesOrderNo}
            date={listQuery.query.date}
            handleSearch={handleSearch}
          />
        </ControllerRow>
        <Pager
          currentPage={listQuery.query.offset / listQuery.query.limit}
          allPage={Math.ceil(listQuery?.allCount / listQuery.query.limit)}
          topPager={true}
          bottomPager={true}
          handleChangePage={changePage}
        >
          <ServiceHistoryTable
            rowNumberInit={listQuery.query.offset}
            histories={listQuery?.results}
            hasError={errorInLoading}
          />
        </Pager>
      </>
    </PageLayout>
  );
};
